import { SkeletonAvatar } from "../../utils/SkeletonLoader";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GLOBAL_IMAGES } from "../../assets/asset";
import useProfileAvatars from "../../features/queryHooks/useProfileAvatars";
import { useUpdateUser } from "../../features/auth/useLogin";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setIsNewUser } from "../../features/auth/user.reducer";
import {
  getAfterLoginInitialStep,
  getProfileCompletionState,
} from "../../features/auth/profileCompletion";

const AfterLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loginData } = location.state || {};
  const { user } = useSelector((state) => state.user);
  const onboardingProfile = loginData || user;
  const {
    normalizedUserName,
    normalizedDob,
    normalizedAvatar,
    isNameMissing,
    isDobMissing,
  } = getProfileCompletionState(onboardingProfile);
  const profileAvatarQuery = useProfileAvatars();
  const updateUserMutation = useUpdateUser();
  const [currentStep, setCurrentStep] = useState(() =>
    getAfterLoginInitialStep(onboardingProfile)
  );
  const [formData, setFormData] = useState(() => ({
    username: normalizedUserName,
    dob: normalizedDob,
    profilephoto: normalizedAvatar,
  }));
  const [formErrors, setFormErrors] = useState({
    dob: "",
  });

  const calculateDateLimits = () => {
    const today = new Date();
    const maxDate = new Date(
      today.getFullYear() - 18,
      today.getMonth(),
      today.getDate()
    );

    const minDate = new Date(
      today.getFullYear() - 60,
      today.getMonth(),
      today.getDate()
    );

    return {
      max: maxDate.toISOString().split("T")[0],
      min: minDate.toISOString().split("T")[0],
    };
  };

  const dateLimits = calculateDateLimits();

  useEffect(() => {
    setCurrentStep(isNameMissing ? 1 : isDobMissing ? 2 : 3);
    setFormData({
      username: normalizedUserName,
      dob: normalizedDob,
      profilephoto: normalizedAvatar,
    });
  }, [
    isNameMissing,
    isDobMissing,
    normalizedUserName,
    normalizedDob,
    normalizedAvatar,
  ]);

  const newUpdateSignup = async () => {
    if (!isValidDob(formData.dob)) {
      setCurrentStep(2);
      setFormErrors((prev) => ({
        ...prev,
        dob: "Please enter a valid date of birth",
      }));
      return;
    }

    updateUserMutation.mutate(
      {
        id: user?.UserId || user?.pk,
        user_name: formData.username,
        parents_date_of_birth: formData.dob,
        profilephotos: formData.profilephoto,
      },
      {
        onSuccess: () => {
          navigate("/yearsdashboard");
          dispatch(setIsNewUser(true));
        },
        onError: (error) => {
          const serverMessage = error?.response?.data?.message || "";
          if (
            serverMessage === "Request data are incorrect" ||
            serverMessage.toLowerCase().includes("date")
          ) {
            setCurrentStep(2);
            setFormErrors((prev) => ({
              ...prev,
              dob: "Please enter a valid date of birth",
            }));
            return;
          }
          toast.error("Failed to update user data");
        },
      }
    );
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === "dob") {
      setFormErrors((prev) => ({ ...prev, dob: "" }));
    }
  };

  const handleNext = async () => {
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleAvatarSelect = async (avatarUrl) => {
    setFormData({ ...formData, profilephoto: avatarUrl });
  };

  const isValidDob = (dob) => {
    if (!dob) return false;
    if (dob < dateLimits.min || dob > dateLimits.max) return false;
    return true;
  };

  const handleDobContinue = () => {
    if (!isValidDob(formData.dob)) {
      setFormErrors((prev) => ({
        ...prev,
        dob: "Please enter a valid date of birth",
      }));
      return;
    }

    setFormErrors((prev) => ({ ...prev, dob: "" }));
    handleNext();
  };

  const buttonStyles =
    "text-white bg-[#08059B] px-[50px] py-[7px] rounded-full";
  const backButtonStyles =
    "text-[#08059B] border border-[#08059B] px-[50px] py-[7px] rounded-full mr-4";

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-blue-50 font-mali"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <img
        className="w-[20%] mb-8"
        src={GLOBAL_IMAGES.dingoLogoText}
        alt="Dingo Text Logo"
      />

      <div className="bg-white bg-opacity-90 p-8 rounded-[20px] shadow-lg max-w-md w-full">
        {currentStep === 1 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold mb-6">
              Enter your Kid's username:
            </h2>
            <input
              name="username"
              placeholder="Enter username"
              value={formData.username}
              onChange={handleChange}
              className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex justify-end mt-6">
              <button
                onClick={handleNext}
                disabled={!formData.username.trim()}
                className={`${buttonStyles} ${
                  !formData.username.trim()
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <>
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold mb-6">
                Enter your date of birth:
              </h2>
              <input
                name="dob"
                type="date"
                value={formData.dob}
                onChange={handleChange}
                min={dateLimits.min}
                max={dateLimits.max}
                className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {formErrors.dob && (
                <p className="text-red-600 text-sm mt-1">{formErrors.dob}</p>
              )}
              <div className="flex justify-center mt-6">
                <button onClick={handleBack} className={backButtonStyles}>
                  Back
                </button>
                <button
                  onClick={handleDobContinue}
                  className={buttonStyles}
                >
                  Continue
                </button>
              </div>
            </div>
          </>
        )}

        {currentStep === 3 && (
          <>
            {profileAvatarQuery.isPending ? (
              <SkeletonAvatar />
            ) : (
              <div className="space-y-4">
                <h2 className="text-2xl font-semibold mb-6">
                  Choose an Avatar:
                </h2>
                <div className="grid grid-cols-5 gap-4">
                  {profileAvatarQuery?.data?.data.map((item, i) => (
                    <img
                      key={i}
                      src={item.fields.avatarUrl}
                      alt={`Avatar ${i}`}
                      className={`w-full aspect-square rounded-full cursor-pointer transition-all ${
                        formData.profilephoto === item.fields.avatarUrl
                          ? "ring-4 ring-blue-500 scale-110"
                          : "hover:scale-105"
                      }`}
                      onClick={() => handleAvatarSelect(item.fields.avatarUrl)}
                    />
                  ))}
                </div>
                <div className="flex justify-center mt-6">
                  <button onClick={handleBack} className={backButtonStyles}>
                    Back
                  </button>
                  <button
                    onClick={newUpdateSignup}
                    disabled={!formData.profilephoto}
                    className={`${buttonStyles} ${
                      !formData.profilephoto
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    Get Started
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AfterLogin;
